import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import Header from './components/Header';
import Hero from './components/Hero';
import About from './components/About';
import Services from './components/Services';
import Callout from './components/Callout';
import Contact from './components/Contact';
import Footer from './components/Footer';
import PrivacyPolicy from './components/PrivacyPolicy';

const AppContent = ({ children }) => {
  const { language } = useLanguage();

  useEffect(() => {
    document.title = language === 'fr' ? 'Groupe Garant' : 'Garant Group';
  }, [language]);

  // Simple entry animation for non-background content
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // Add staggered delay based on element's position in its section
          const delay = parseInt(entry.target.dataset.animationDelay) || 0;
          setTimeout(() => {
            entry.target.classList.add('animate-in');
          }, delay);
          observer.unobserve(entry.target); // Only animate once per intersection
        }
      });
    }, {
      threshold: 0,
      rootMargin: '0px 0px 0px 0px'
    });

    // Reset animations when navigating to home page
    const resetAnimations = () => {
      const contentElements = document.querySelectorAll('.content-entry-animation');
      contentElements.forEach((element) => {
        element.classList.remove('animate-in');
      });
    };

    // Add entry animation to content elements that have the class
    const setupAnimations = () => {
      resetAnimations(); // Clear any existing animations
      
    const contentElements = document.querySelectorAll('.content-entry-animation');
    let sectionElementIndex = {};
    
    contentElements.forEach((element) => {
      // Skip elements that are in the hero section (top of page)
      const heroSection = element.closest('#home');
      if (!heroSection) {
        // Find which section this element belongs to
        const section = element.closest('section');
        const sectionId = section ? section.id : 'default';
        
        // Initialize counter for this section if it doesn't exist
        if (!sectionElementIndex[sectionId]) {
          sectionElementIndex[sectionId] = 0;
        }
        
        // Add staggered delay based on element order within section (50ms apart)
        element.dataset.animationDelay = sectionElementIndex[sectionId] * 50;
        sectionElementIndex[sectionId]++;
        
        observer.observe(element);
      }
    });
    };

    // Setup animations on component mount and when location changes
    const timeoutId = setTimeout(setupAnimations, 100);

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [window.location.pathname]); // Re-run when pathname changes

  return children;
};

const HomePage = () => {
  return (
    <div className="App">
      <Header />
      <Hero />
      <About />
      <Services />
      <Callout />
      <Contact />
      <Footer />
    </div>
  );
};

const PrivacyPolicyPage = () => {
  return (
    <div className="App">
      <Header />
      <PrivacyPolicy />
    </div>
  );
};

function App() {
  return (
    <LanguageProvider>
      <Router>
        <Routes>
          <Route path="/" element={<AppContent><HomePage /></AppContent>} />
          <Route path="/privacy-policy" element={<AppContent><PrivacyPolicyPage /></AppContent>} />
        </Routes>
      </Router>
    </LanguageProvider>
  );
}

export default App; 
